import { IFormStep } from '../../utils/formStep';
import React, { useEffect, useState } from 'react';
import { TFunction, withTranslation } from 'react-i18next';
import OrganizationStep1Reasons from './organizationStep1Reasons';
import OrganizationStep1OrganizationProfile from './organizationStep1OrganizationProfile';
import OrganizationStep1AdditionalInformation from './organizationStep1AdditionalInformation';
import OrganizationStep1BusinessInformations from './organizationStep1BusinessInformations';
import fetchFormData, { IFormDataModel } from '../../../../helpers/fetchFormData';
import { organizationModelFromFormData } from '../../../../helpers/models/organizationModel';
import { IProvinceModel } from '../../../../helpers/models/provinceModel';
import getProvinces from '../../../../helpers/provinces';
import { updateOrganization } from '../../../../helpers/organization';
import { updateAccount } from '../../../../helpers/account';
import { isErrorModel } from '../../../../helpers/models/errorModel';
import {
    handleRequiredError,
    scrollToFormErrors,
    validateEmail,
    validatePca,
    validatePhoneExt,
    validatePhoneNumber,
    validatePositiveInt,
    validatePostalCode,
} from '../../utils/formErrors';
import { IRoleModel } from '../../../../helpers/models/roleModel';
import {
    IOrganizationStep1FormResultModel,
    OrganizationStep1FormResultKeys,
} from './organizationStep1FormModel';
import { INotificationErrorMessage } from '../../../notification/notification';
import FormDescriptionModal from '../../../modal/instances/formDescriptionModal';
import FormSoftwareModal from '../../../modal/instances/formSoftwareModal';
import { IFormModalRef } from '../../../modal/modal';
import { startOverId } from '../../registration';

const OrganizationStep1 = (props: IFormStep) => {
    const { t, startOver, account, i18n } = props;

    const [provinces, setProvinces] = useState<IProvinceModel[]>([]);
    const [introModal, setIntroModal] = useState<JSX.Element | undefined>();
    const [skipSoftwareCommunicationCheck, setSkipSoftwareCommunicationCheck] =
        useState<boolean>(false);
    const ref = React.createRef<IFormModalRef>();

    useEffect(() => {
        if (skipSoftwareCommunicationCheck) {
            ref.current?.setModalOpen(false);
            document.getElementById('form_submit_button')!.click();
        }
    }, [skipSoftwareCommunicationCheck]);

    useEffect(() => {
        async function getData() {
            const provincesList = await getProvinces(i18n.language);
            if (provincesList) {
                setProvinces(provincesList);
            }

            if (!localStorage.getItem(account.id)) {
                setIntroModal(
                    <FormDescriptionModal name={account.firstname} type={account.type} />
                );
                localStorage.setItem(account.id, 'seen');
            }
        }
        getData();
    }, [i18n.language]);

    const handleClientFormValidation = (
        formResult: IOrganizationStep1FormResultModel,
        data: IFormDataModel[],
        t: TFunction
    ) => {
        let errorList: INotificationErrorMessage[] = [];

        // Required fields
        errorList = handleRequiredError(formResult, data, t).concat(errorList);

        // Custom validation
        if (formResult.form_organization_step_1_field_phone_ext) {
            const extValidation = validatePhoneExt(
                OrganizationStep1FormResultKeys.PHONE_EXT,
                formResult.form_organization_step_1_field_phone_ext,
                t
            );
            if (extValidation) {
                errorList.push({
                    message: extValidation,
                    focusElement: OrganizationStep1FormResultKeys.PHONE_EXT,
                    weight: 9,
                });
            }
        }

        if (formResult.form_organization_step_1_field_phone) {
            const phoneValidation = validatePhoneNumber(
                OrganizationStep1FormResultKeys.PHONE,
                formResult.form_organization_step_1_field_phone,
                t
            );
            if (phoneValidation) {
                errorList.push({
                    message: phoneValidation,
                    focusElement: OrganizationStep1FormResultKeys.PHONE,
                    weight: 8,
                });
            }
        }

        if (formResult.form_organization_step_1_field_postal_code) {
            const postalCodeValidation = validatePostalCode(
                OrganizationStep1FormResultKeys.POSTAL_CODE,
                formResult.form_organization_step_1_field_postal_code,
                t
            );
            if (postalCodeValidation) {
                errorList.push({
                    message: postalCodeValidation,
                    focusElement: OrganizationStep1FormResultKeys.POSTAL_CODE,
                    weight: 7,
                });
            }
        }

        if (formResult.form_organization_step_1_field_enterprise_number) {
            const enterpriseNumberValidation = validatePositiveInt(
                OrganizationStep1FormResultKeys.ENTERPRISE_NUMBER,
                +formResult.form_organization_step_1_field_enterprise_number,
                t
            );

            if (enterpriseNumberValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_organization_step_1_enterprise_number',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: OrganizationStep1FormResultKeys.ENTERPRISE_NUMBER,
                    weight: 12,
                });
            }
        }

        if (formResult.form_organization_step_1_field_enterprise_tps) {
            const tpsValidation = validatePositiveInt(
                OrganizationStep1FormResultKeys.TPS,
                +formResult.form_organization_step_1_field_enterprise_tps,
                t
            );
            if (tpsValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_organization_step_1_enterprise_tps',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: OrganizationStep1FormResultKeys.TPS,
                    weight: 13,
                });
            }
        }
        if (formResult.form_organization_step_1_field_email) {
            const emailValidation = validateEmail(
                OrganizationStep1FormResultKeys.EMAIL,
                formResult.form_organization_step_1_field_email,
                t
            );
            if (emailValidation) {
                errorList.push({
                    message: emailValidation,
                    focusElement: OrganizationStep1FormResultKeys.EMAIL,
                    weight: 3,
                });
            }
        }
        if (formResult.form_organization_step_1_field_enterprise_rt) {
            const rtValidation = validatePositiveInt(
                OrganizationStep1FormResultKeys.RT,
                +formResult.form_organization_step_1_field_enterprise_rt,
                t
            );
            if (rtValidation) {
                errorList.push({
                    messagePrefix: 'form_generic_positive_int_error_prefix',
                    message: 'form_organization_step_1_enterprise_rt',
                    messageSuffix: 'form_generic_positive_int_error_suffix',
                    lowerCaseMessage: true,
                    focusElement: OrganizationStep1FormResultKeys.RT,
                    weight: 14,
                });
            }
        }

        const pcaValidation = validatePca(
            OrganizationStep1FormResultKeys.PCA,
            {
                address: formResult.form_organization_step_1_field_address,
                city: formResult.form_organization_step_1_field_city,
                postal_code: formResult.form_organization_step_1_field_postal_code,
            },
            t
        );
        if (pcaValidation) {
            errorList.push({
                message: pcaValidation,
                focusElement: OrganizationStep1FormResultKeys.PCA,
                weight: 15,
            });
        }

        return errorList;
    };

    const handleSubmitOrganizationStep1 = async (e: any) => {
        e.preventDefault();
        const data = fetchFormData(e);
        const formResult = Object.assign(
            {},
            ...data.map((x) => ({ [x.name]: x.value }))
        ) as IOrganizationStep1FormResultModel;
        const details = organizationModelFromFormData(formResult);
        const errorList = handleClientFormValidation(formResult, data, t);

        if (!errorList.length) {
            const softwareSelectElement = document.getElementById(
                OrganizationStep1FormResultKeys.MANAGING_SOFTWARE
            )! as HTMLSelectElement;
            if (
                !skipSoftwareCommunicationCheck &&
                softwareSelectElement.options[softwareSelectElement.selectedIndex].getAttribute(
                    'data-datatype'
                ) === 'enabled' &&
                !formResult.form_organization_step_1_field_authorization_communication
            ) {
                ref.current?.setModalOpen(true);
                return;
            }

            details.province = Number(
                provinces.find(
                    (province) =>
                        province.name === formResult.form_organization_step_1_field_province
                )?.id
            );

            details.nonClinical =
                data.find(
                    (item) =>
                        item.name === OrganizationStep1FormResultKeys.CLINICAL_ENV && item.checked
                )?.value === '1';
            details.ownershipChange =
                data.find(
                    (item) =>
                        item.name === OrganizationStep1FormResultKeys.PROPERTY_TRANSFER &&
                        item.checked
                )?.value === '1';
            details.ownershipChangeDate = details.ownershipChange
                ? formResult.form_organization_step_1_field_enterprise_property_transfer_date
                : '';

            details.legalNameChange =
                data.find(
                    (item) =>
                        item.name === OrganizationStep1FormResultKeys.LEGAL_NAME_CHANGE &&
                        item.checked
                )?.value === '1';
            details.legalNameChangeDate = details.legalNameChange
                ? formResult.form_organization_step_1_field_enterprise_legal_name_change_date
                : '';

            // update organization
            const organizationResult = await updateOrganization(i18n.language, account.id, {
                ...details,
            });
            if (isErrorModel(organizationResult)) {
                errorList.push({ message: 'form_generic_error', weight: 0 });
            } else {
                // update step in account
                const newFormData = Object.assign({}, account);
                if (newFormData.step >= 1) {
                    newFormData.step++;
                }

                // set number to province and role instead of the returned object
                newFormData.province = +(account.province as IProvinceModel).id;
                newFormData.role = +(account.role as IRoleModel).id;

                // set reasons retrieve to account
                newFormData.reason = formResult.form_organization_step_1_field_register_reason;
                newFormData.reasonOther = formResult.form_organization_step_1_field_reason_other;

                await updateAccount(i18n.language, account.id, { ...newFormData });
            }
        }
        if (errorList.length) {
            props.setError(errorList);
            scrollToFormErrors();
        }
    };

    return (
        <>
            {introModal}
            <FormSoftwareModal
                onCloseAndContinue={(e: any) => setSkipSoftwareCommunicationCheck(true)}
                ref={ref}
            />
            <form className="registration-container" onSubmit={handleSubmitOrganizationStep1}>
                <OrganizationStep1OrganizationProfile account={props.account} />
                <OrganizationStep1BusinessInformations account={props.account} />
                <OrganizationStep1AdditionalInformation account={props.account} />
                <OrganizationStep1Reasons account={props.account} />

                <div className="registration-organization-button-container">
                    <button
                        className="button button--secondary registration-organization-button"
                        onClick={startOver}
                        id={startOverId}
                    >
                        {t('form_organization_step_1_button_restart')}
                    </button>
                    <button
                        className="button button--primary registration-organization-button"
                        type="submit"
                        id="form_submit_button"
                    >
                        {t('form_organization_step_1_button_next')}
                    </button>
                </div>
            </form>
        </>
    );
};

export default withTranslation()(OrganizationStep1);
